import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Divider,
} from "@mui/material";
import {
  activateTenantOrganization,
  createTenantOrganization,
  fetchAutomationRoutingOptions,
  getTenantOrganization,
  listOrganizationMemberships,
  listTenantOrganizations,
  removeMembership,
  suspendTenantOrganization,
  updateTenantOrganization,
  upsertMembership,
} from "../../services/organizationService";
import ConfirmationModel from "../../global/components/ConfirmationModal/confirmationModel";
import {
  openErrorNotification,
  openSuccessNotification,
  isTruthy,
} from "../../helpers/methods";
import { useSelector } from "react-redux";
import {
  selectActiveOrganizationId,
  selectPrivileges,
} from "../../redux/authSlice";
import { useHistory } from "react-router-dom";
import urls from "../../global/constants/UrlConstants";
import { canAccessOrganizations } from "../../Shared/AppDrawer/menuPrivileges";
import YhInput from "../../ui/YhInput";
import YhSelect from "../../ui/YhSelect";
import YhButton from "../../ui/YhButton";
import profileImg from "../../assets/images/user-default.svg";
import { ImageUpLoad } from "../Team/components/stepper/EmployeeDocuments/EmployeeDocumentServices";
import {
  getCity,
  getCountry,
  getState,
  getTimeZone,
} from "../Team/components/stepper/Personal Details/PersonalDetailsService";
import notifiers from "../../global/constants/NotificationConstants";
import customErrorHandler from "../../utils/customErrorHandler";
import { getAllUsers } from "../Team/components/ShowAll/ShowAllService";
import Autocomplete from "@mui/material/Autocomplete";

type Field = { value: string; error: string; id?: string };

const emptyProfile = () => ({
  companyname: { value: "", error: "" } as Field,
  brandname: { value: "", error: "" } as Field,
  company_type: { value: "Real Estate", error: "" } as Field,
  country_id: { value: "", error: "", id: "" } as Field,
  state_id: { value: "", error: "", id: "" } as Field,
  city: { value: "", error: "" } as Field,
  address1: { value: "", error: "" } as Field,
  address2: { value: "", error: "" } as Field,
  zipcode: { value: "", error: "" } as Field,
  website: { value: "", error: "" } as Field,
  time_zone: { value: "", error: "", id: "" } as Field,
  currency: { value: "", error: "" } as Field,
  country_code: { value: "", error: "" } as Field,
  imageUrl: { value: "", error: "" } as Field,
});

const orgToProfile = (org: any) => ({
  companyname: {
    value: org.legalName || org.name || "",
    error: "",
  } as Field,
  brandname: { value: org.brandName || org.name || "", error: "" } as Field,
  company_type: {
    value: org.companyType || "Real Estate",
    error: "",
  } as Field,
  country_id: {
    value: org.countryId || "",
    id: org.countryId || "",
    error: "",
  } as Field,
  state_id: {
    value: org.stateId || "",
    id: org.stateId || "",
    error: "",
  } as Field,
  city: { value: org.city || "", error: "" } as Field,
  address1: { value: org.address1 || "", error: "" } as Field,
  address2: { value: org.address2 || "", error: "" } as Field,
  zipcode: { value: org.zipcode || "", error: "" } as Field,
  website: { value: org.website || "", error: "" } as Field,
  time_zone: {
    value: org.timeZoneId || org.timezone || "",
    id: org.timeZoneId || "",
    error: "",
  } as Field,
  currency: { value: org.currency || "", error: "" } as Field,
  country_code: { value: org.countryCode || "", error: "" } as Field,
  imageUrl: { value: org.logoUrl || "", error: "" } as Field,
});

const OrganizationsAdmin: React.FC = () => {
  const privileges = useSelector(selectPrivileges);
  const activeOrganizationId = useSelector(selectActiveOrganizationId);
  const canManage = canAccessOrganizations(privileges || {});
  const history = useHistory();

  useEffect(() => {
    if (!canManage) {
      openErrorNotification(
        "You do not have permission to manage Organizations"
      );
      history.replace(urls.dashboardViewPath);
    }
  }, [canManage, history]);

  const [orgs, setOrgs] = useState<any[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [profile, setProfile] = useState(emptyProfile());
  const [previewImage, setPreviewImage] = useState<any>(profileImg);
  const [country, setCountry] = useState<any[]>([]);
  const [stateList, setStateList] = useState<any[]>([]);
  const [cityList, setCityList] = useState<any[]>([]);
  const [timeZone, setTimeZone] = useState<any[]>([]);
  const [savingProfile, setSavingProfile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [loading, setLoading] = useState(false);
  const [adminMode, setAdminMode] = useState<"none" | "new" | "existing">(
    "none"
  );
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminName, setAdminName] = useState("");
  const [adminPhone, setAdminPhone] = useState("");
  const [createAdminUser, setCreateAdminUser] = useState<{
    id: string;
    label: string;
    email: string;
  } | null>(null);
  const [memberOrgId, setMemberOrgId] = useState("");
  const [memberUserId, setMemberUserId] = useState("");
  const [memberEmail, setMemberEmail] = useState("");
  const [memberRole, setMemberRole] = useState("org_admin");
  const [crmUsers, setCrmUsers] = useState<
    { id: string; label: string; email: string }[]
  >([]);
  const [selectedMember, setSelectedMember] = useState<{
    id: string;
    label: string;
    email: string;
  } | null>(null);
  const [orgMembers, setOrgMembers] = useState<any[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState("");
  const [memberToRemove, setMemberToRemove] = useState<any | null>(null);

  const [maskEnabled, setMaskEnabled] = useState(true);
  const [visibleDigits, setVisibleDigits] = useState(4);
  const [allowRoles, setAllowRoles] = useState<string[]>([
    "super_admin",
    "org_admin",
    "sub_admin",
  ]);
  const [allowTeamIds, setAllowTeamIds] = useState<string[]>([]);
  const [allowUserIds, setAllowUserIds] = useState<string[]>([]);
  const [routingTeams, setRoutingTeams] = useState<
    { teamId: string; teamName: string }[]
  >([]);
  const [routingMembers, setRoutingMembers] = useState<
    { userId: string; name: string; email: string }[]
  >([]);
  const [savingMask, setSavingMask] = useState(false);

  const loadOrgs = useCallback(async () => {
    try {
      const res = await listTenantOrganizations();
      const ok = res?.success === 1 || res?.success === true;
      if (!ok) {
        openErrorNotification(res?.message || "Failed to load organizations");
        setOrgs([]);
        return;
      }
      const raw = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
          ? res.data.data
          : [];
      const list = raw
        .map((o: any) => ({
          ...o,
          _id: String(o?._id || o?.id || ""),
          name: o?.name || o?.brandName || o?.legalName || "",
          brandName: o?.brandName || o?.name || "",
          legalName: o?.legalName || o?.name || "",
          slug: o?.slug || "",
          status: o?.status || "active",
        }))
        .filter((o: any) => o._id && o._id !== "undefined");
      setOrgs(list);
      setSelectedOrgId((prev) => {
        if (prev && list.some((o: any) => o._id === prev)) return prev;
        const preferred =
          (activeOrganizationId &&
            activeOrganizationId !== "all" &&
            list.find(
              (o: any) => o._id === String(activeOrganizationId)
            )?._id) ||
          list.find((o: any) => o.slug === "yathaw")?._id ||
          list[0]?._id ||
          "";
        return preferred || "";
      });
      setMemberOrgId((prev) => {
        if (prev && list.some((o: any) => o._id === prev)) return prev;
        return list[0]?._id || "";
      });
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to load organizations");
      setOrgs([]);
    }
  }, [activeOrganizationId]);

  useEffect(() => {
    loadOrgs();
  }, [loadOrgs]);

  const loadOrgMembers = useCallback(async (orgId: string) => {
    if (!orgId || orgId === "all") {
      setOrgMembers([]);
      return;
    }
    setLoadingMembers(true);
    try {
      const res = await listOrganizationMemberships(orgId);
      if (res?.success === 1) {
        setOrgMembers(Array.isArray(res.data) ? res.data : []);
      } else {
        setOrgMembers([]);
      }
    } catch (_) {
      setOrgMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  }, []);

  useEffect(() => {
    if (!canManage) return;
    const orgId = memberOrgId || selectedOrgId;
    if (orgId) loadOrgMembers(orgId);
  }, [canManage, memberOrgId, selectedOrgId, loadOrgMembers]);

  useEffect(() => {
    if (!canManage) return;
    (async () => {
      try {
        const res = await getAllUsers({
          pageNumber: 0,
          pageSize: 2000,
          search: "",
        });
        const rows = res?.data || res?.message?.data || [];
        const list = (Array.isArray(rows) ? rows : [])
          .map((u: any) => ({
            id: String(u._id),
            email: String(u.email || "").trim(),
            label: `${u.name || u.fullName || "User"}${
              u.email ? ` (${u.email})` : ""
            }`,
          }))
          .filter((u: any) => u.id && u.email);
        setCrmUsers(list);
      } catch (_) {
        setCrmUsers([]);
      }
    })();
  }, [canManage]);

  const loadProfile = useCallback(async (orgId: string) => {
    if (!orgId || orgId === "undefined" || orgId === "all") return;
    try {
      const res = await getTenantOrganization(orgId);
      const ok = res?.success === 1 || res?.success === true;
      if (ok && res.data) {
        const next = orgToProfile(res.data);
        setProfile(next);
        setPreviewImage(res.data.logoUrl || profileImg);

        const pm = res.data.settings?.phoneMasking || {};
        setMaskEnabled(pm.enabled !== false);
        setVisibleDigits(Number(pm.visibleDigits) > 0 ? Number(pm.visibleDigits) : 4);
        setAllowRoles(
          Array.isArray(pm.allowFullForRoles) && pm.allowFullForRoles.length
            ? pm.allowFullForRoles.map(String)
            : ["super_admin", "org_admin", "sub_admin"]
        );
        setAllowTeamIds(
          Array.isArray(pm.allowFullForTeamIds)
            ? pm.allowFullForTeamIds.map(String)
            : []
        );
        setAllowUserIds(
          Array.isArray(pm.allowFullForUserIds)
            ? pm.allowFullForUserIds.map(String)
            : []
        );

        // Prefetch country / state / timezone options so selects show labels
        try {
          const cRes = await getCountry();
          const countries = cRes?.data || [];
          setCountry(countries);
          if (next.country_id.id) {
            const sRes = await getState(next.country_id.id);
            setStateList(sRes?.data || []);
            const tRes = await getTimeZone(next.country_id.id);
            setTimeZone(tRes?.data || []);
          }
        } catch (_) {
          /* ignore option prefetch errors */
        }

        try {
          const opt = await fetchAutomationRoutingOptions(orgId);
          const data = opt?.data || {};
          setRoutingTeams(Array.isArray(data.teams) ? data.teams : []);
          setRoutingMembers(Array.isArray(data.members) ? data.members : []);
        } catch (_) {
          setRoutingTeams([]);
          setRoutingMembers([]);
        }
      }
    } catch (e: any) {
      openErrorNotification(e?.message || notifiers.GENERIC_ERROR);
    }
  }, []);

  useEffect(() => {
    if (selectedOrgId) loadProfile(selectedOrgId);
  }, [selectedOrgId, loadProfile]);

  const handleCreate = async () => {
    if (!name) {
      openErrorNotification("Company legal name is required");
      return;
    }
    if (adminMode === "new") {
      if (!adminEmail.trim() || !adminPassword.trim()) {
        openErrorNotification(
          "Organization admin email and password are required"
        );
        return;
      }
      if (adminPassword.trim().length < 6) {
        openErrorNotification("Admin password must be at least 6 characters");
        return;
      }
    } else if (adminMode === "existing" && !createAdminUser?.id) {
      openErrorNotification(
        "Select an existing team member as organization admin"
      );
      return;
    }
    const autoSlug =
      slug ||
      name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
    setLoading(true);
    try {
      const payload: any = {
        name,
        slug: autoSlug,
        brandName: name,
        legalName: name,
        companyType: "Real Estate",
      };
      if (adminMode === "new") {
        payload.adminEmail = adminEmail.trim();
        payload.adminPassword = adminPassword;
        payload.adminName = adminName.trim() || undefined;
        payload.adminPhone = adminPhone.trim() || undefined;
      } else if (adminMode === "existing") {
        payload.adminUserId = createAdminUser!.id;
      }
      const res = await createTenantOrganization(payload);
      if (res?.success === 1) {
        openSuccessNotification(
          adminMode === "new"
            ? "Organization created with new org admin account"
            : adminMode === "existing"
              ? "Organization created — selected user is org admin"
              : "Organization created — assign an admin later if needed"
        );
        setName("");
        setSlug("");
        setAdminEmail("");
        setAdminPassword("");
        setAdminName("");
        setAdminPhone("");
        setCreateAdminUser(null);
        setAdminMode("none");
        await loadOrgs();
        if (res.data?._id) setSelectedOrgId(String(res.data._id));
      } else {
        openErrorNotification(res?.message || "Failed to create");
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (org: any) => {
    const res =
      org.status === "suspended"
        ? await activateTenantOrganization(org._id)
        : await suspendTenantOrganization(org._id);
    if (res?.success === 1) {
      openSuccessNotification(
        org.status === "suspended" ? "Activated" : "Suspended"
      );
      loadOrgs();
    } else {
      openErrorNotification(res?.message || "Failed");
    }
  };

  const assignMember = async () => {
    if (!memberOrgId || (!memberUserId && !memberEmail)) {
      openErrorNotification("Select organization and a CRM user");
      return;
    }
    const already = orgMembers.some(
      (m) =>
        String(m.userId) === String(memberUserId) ||
        (memberEmail &&
          String(m.user?.email || "").toLowerCase() ===
            memberEmail.trim().toLowerCase())
    );
    const res = await upsertMembership({
      userId: memberUserId || undefined,
      email: memberEmail.trim() || undefined,
      organizationId: memberOrgId,
      roleSlug: memberRole,
      isPrimary: true,
      canViewAuditLogs: memberRole === "org_admin" || memberRole === "sub_admin",
    });
    if (res?.success === 1) {
      openSuccessNotification(
        already
          ? `Updated ${
              selectedMember?.label || memberEmail
            } to ${memberRole}. Ask them to log in again to refresh access.`
          : `Assigned ${
              selectedMember?.label || memberEmail
            } as ${memberRole}. They will only see this org after login.`
      );
      setMemberEmail("");
      setMemberUserId("");
      setSelectedMember(null);
      await loadOrgMembers(memberOrgId);
    } else {
      openErrorNotification(res?.message || "Failed to assign");
    }
  };

  const memberRemoveLabel = (membership: any) =>
    membership?.user?.name ||
    membership?.user?.email ||
    String(membership?.userId || "user");

  const confirmUnassignMember = async () => {
    const membership = memberToRemove;
    const id = String(membership?._id || "");
    if (!id) {
      setMemberToRemove(null);
      return;
    }
    const label = memberRemoveLabel(membership);
    setRemovingMemberId(id);
    try {
      const res = await removeMembership(id);
      if (res?.success === 1) {
        openSuccessNotification(`Removed ${label} from organization`);
        setMemberToRemove(null);
        await loadOrgMembers(memberOrgId || selectedOrgId);
      } else {
        openErrorNotification(res?.message || "Failed to remove");
      }
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to remove");
    } finally {
      setRemovingMemberId("");
    }
  };

  const handleOnChangeInputField = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setProfile({
      ...profile,
      [event.target.name]: { value: event.target.value, error: "" },
    });
  };

  const handleAutocompleteChange = (name: string, key: any, value: any) => {
    if (!value) return;
    if (key === "country_name") {
      setProfile((prev) => ({
        ...prev,
        [name]: { value: value[key], error: "", id: value._id },
        country_code: { value: value.country_code || "", error: "" },
        currency: { value: value.currency || "", error: "" },
      }));
    }
    if (key === "state_name" || key === "city_name" || key === "time_zone") {
      setProfile((prev) => ({
        ...prev,
        [name]: { value: value[key], error: "", id: value._id },
      }));
    }
  };

  const onClickAutoComplet = async (inputType: string) => {
    try {
      if (inputType === "country_id") {
        const response = await getCountry();
        customErrorHandler(response);
        setCountry(response.data || []);
      }
      if (inputType === "state_id") {
        const response = await getState(profile.country_id.id);
        customErrorHandler(response);
        setStateList(response.data || []);
      }
      if (inputType === "city") {
        const response = await getCity(profile.state_id.id);
        customErrorHandler(response);
        setCityList(response.data || []);
      }
      if (inputType === "time_zone") {
        const response = await getTimeZone(profile.country_id.id);
        customErrorHandler(response);
        setTimeZone(response.data || []);
      }
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error?.message) ? error?.message : notifiers.GENERIC_ERROR
      );
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    const data = new FormData();
    data.append("file", file);
    reader.onload = async (loadEvent: any) => {
      setPreviewImage(loadEvent.target.result);
      try {
        const uploadedUrl = await ImageUpLoad(data);
        setProfile((prev) => ({
          ...prev,
          imageUrl: { error: "", value: uploadedUrl?.imageUrl || "" },
        }));
      } catch (error: any) {
        openErrorNotification(
          isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
        );
      }
    };
    reader.readAsDataURL(file);
  };

  const savePhoneMasking = async () => {
    if (!selectedOrgId || selectedOrgId === "undefined") {
      openErrorNotification("Select an organization first");
      return;
    }
    setSavingMask(true);
    try {
      const res = await updateTenantOrganization(selectedOrgId, {
        settings: {
          phoneMasking: {
            enabled: maskEnabled,
            visibleDigits: Number(visibleDigits) || 4,
            maskChar: "*",
            allowFullForRoles: allowRoles,
            // Org admins (hierarchy 1) see full numbers when org_admin is allowlisted
            allowFullForHierarchyLevels: allowRoles.includes("org_admin")
              ? ["1"]
              : [],
            allowFullForTeamIds: allowTeamIds,
            allowFullForUserIds: allowUserIds,
          },
        },
      });
      if (res?.success === 1 || res?.success === true) {
        const orgLabel =
          selectedOrg?.name || selectedOrg?.brandName || "organization";
        openSuccessNotification(
          `Phone masking saved for ${orgLabel}`
        );
        await loadProfile(selectedOrgId);
      } else {
        openErrorNotification(res?.message || "Failed to save masking settings");
      }
    } catch (e: any) {
      openErrorNotification(e?.message || notifiers.GENERIC_ERROR);
    } finally {
      setSavingMask(false);
    }
  };

  const saveCompanyDetails = async () => {
    if (!selectedOrgId) {
      openErrorNotification("Select an organization first");
      return;
    }
    const required: Array<keyof typeof profile> = [
      "companyname",
      "brandname",
      "address1",
      "city",
      "zipcode",
      "website",
    ];
    let hasError = false;
    const next = { ...profile };
    required.forEach((k) => {
      if (!String(next[k]?.value || "").trim()) {
        next[k] = { ...next[k], error: "Required" };
        hasError = true;
      }
    });
    if (!next.country_id.id) {
      next.country_id = { ...next.country_id, error: "Required" };
      hasError = true;
    }
    if (!next.state_id.id) {
      next.state_id = { ...next.state_id, error: "Required" };
      hasError = true;
    }
    if (hasError) {
      setProfile(next);
      return;
    }

    setSavingProfile(true);
    try {
      const body = {
        name: profile.brandname.value || profile.companyname.value,
        legalName: profile.companyname.value,
        brandName: profile.brandname.value,
        logoUrl: profile.imageUrl.value,
        companyType: profile.company_type.value || "Real Estate",
        address1: profile.address1.value,
        address2: profile.address2.value,
        city: profile.city.value,
        zipcode: profile.zipcode.value,
        website: profile.website.value,
        countryId: profile.country_id.id,
        stateId: profile.state_id.id,
        countryCode: profile.country_code.value,
        currency: profile.currency.value,
        timeZoneId: profile.time_zone.id || profile.time_zone.value,
        timezone: profile.time_zone.value || undefined,
      };
      const res = await updateTenantOrganization(selectedOrgId, body);
      if (res?.success === 1) {
        openSuccessNotification("Company details saved");
        await loadOrgs();
        await loadProfile(selectedOrgId);
      } else {
        openErrorNotification(res?.message || "Failed to save");
      }
    } catch (e: any) {
      openErrorNotification(e?.message || notifiers.GENERIC_ERROR);
    } finally {
      setSavingProfile(false);
    }
  };

  const countryId = profile?.country_id?.id || "";
  const stateId = profile?.state_id?.id || "";
  const timeZoneId = profile?.time_zone?.id || "";
  const logoSrc =
    profile?.imageUrl?.value && profile.imageUrl.value !== "Https//:"
      ? profile.imageUrl.value
      : previewImage || profileImg;

  const selectedOrg = orgs.find((o) => String(o._id) === String(selectedOrgId));

  if (!canManage) {
    return null;
  }

  return (
    <Box
      sx={{
        height: "calc(100vh - 68px)",
        overflow: "auto",
        p: 3,
        boxSizing: "border-box",
        pb: 6,
      }}
    >
      <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 600 }}>
        Organizations
      </Typography>
      <Typography sx={{ mb: 2, color: "#6B7280", fontSize: 13 }}>
        Update company details and branding. Super admins can also create
        tenants, assign memberships, and suspend organizations.
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 280, mb: 2 }}>
          <InputLabel>Organization</InputLabel>
          <Select
            label="Organization"
            value={selectedOrgId || ""}
            displayEmpty
            onChange={(e) => setSelectedOrgId(String(e.target.value))}
            renderValue={(val) => {
              const id = String(val || "");
              if (!id) return "Select organization";
              const match = orgs.find((o) => String(o._id) === id);
              if (!match) return "Select organization";
              return `${match.name || match.brandName || "Organization"}${
                match.slug ? ` (${match.slug})` : ""
              }`;
            }}
          >
            {orgs.map((o) => (
              <MenuItem key={String(o._id)} value={String(o._id)}>
                {o.name || o.brandName || o.legalName || "Organization"}
                {o.slug ? ` (${o.slug})` : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedOrg && (
          <Box
            sx={{
              mb: 2,
              fontSize: 12,
              color: "#6B7280",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            Status:{" "}
            <Chip
              size="small"
              label={selectedOrg.status || "active"}
              color={selectedOrg.status === "active" ? "success" : "default"}
            />
          </Box>
        )}

        <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-[#111827]">
              Company Details
            </h2>
            <p className="mt-1 text-sm text-[#6B7280]">
              Update legal name, brand, address and logo
            </p>
          </div>
          <div className="relative">
            <div className="h-20 w-20 overflow-hidden rounded-full border border-[#E5E7EB] bg-[#F3F4F6]">
              <img
                src={logoSrc}
                alt="Company logo"
                className="h-full w-full object-cover"
              />
            </div>
            <button
              type="button"
              className="absolute bottom-0 right-0 flex h-8 w-8 items-center justify-center rounded-full border border-[#E5E7EB] bg-white text-[#374151] shadow-sm"
              onClick={() => fileInputRef.current?.click()}
              aria-label="Upload logo"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 15.2A3.2 3.2 0 1 0 12 8.8a3.2 3.2 0 0 0 0 6.4zm8-5.2h-1.2l-.9-1.5A2 2 0 0 0 16.2 7H7.8a2 2 0 0 0-1.7 1.5L5.2 10H4a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2z" />
              </svg>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg"
              className="hidden"
              onChange={handleFileUpload}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <YhInput
            label="Company Type"
            value={profile?.company_type?.value || "Real Estate"}
            disabled
            className="bg-[#E7E7EA]"
          />
          <YhInput
            label="Company Legal Name"
            name="companyname"
            placeholder="eg. XYZ LLC"
            onChange={handleOnChangeInputField}
            value={profile?.companyname?.value || ""}
            error={profile?.companyname?.error}
          />
          <YhInput
            label="Company Brand Name"
            name="brandname"
            placeholder="eg. Yhataw"
            onChange={handleOnChangeInputField}
            value={profile?.brandname?.value || ""}
            error={profile?.brandname?.error}
          />
          <YhSelect
            label="Default Time Zone"
            placeholder="Select time zone"
            value={timeZoneId}
            error={profile?.time_zone?.error}
            options={timeZone.map((tz: any) => ({
              label: tz.time_zone,
              value: tz._id,
            }))}
            disabled={!countryId && !country.length}
            onFocus={() => onClickAutoComplet("time_zone")}
            onChange={(e) => {
              const selected =
                timeZone.find((tz: any) => tz._id === e.target.value) || null;
              if (!selected) return;
              handleAutocompleteChange("time_zone", "time_zone", selected);
            }}
          />
          <YhSelect
            label="Country"
            placeholder="Select country"
            value={countryId}
            error={profile?.country_id?.error}
            options={country.map((c: any) => ({
              label: c.country_name,
              value: c._id,
            }))}
            onFocus={() => onClickAutoComplet("country_id")}
            onChange={(e) => {
              const selected =
                country.find((c: any) => c._id === e.target.value) || null;
              if (!selected) return;
              handleAutocompleteChange("country_id", "country_name", selected);
              setStateList([]);
              setTimeZone([]);
              setCityList([]);
            }}
          />
          <YhSelect
            label="State"
            placeholder="Select state"
            value={stateId}
            error={profile?.state_id?.error}
            options={stateList.map((s: any) => ({
              label: s.state_name,
              value: s._id,
            }))}
            disabled={!countryId && !country.length}
            onFocus={() => onClickAutoComplet("state_id")}
            onChange={(e) => {
              const selected =
                stateList.find((s: any) => s._id === e.target.value) || null;
              if (!selected) return;
              handleAutocompleteChange("state_id", "state_name", selected);
              setCityList([]);
            }}
          />
          <YhInput
            label="City"
            name="city"
            placeholder="enter your city"
            onChange={handleOnChangeInputField}
            value={profile?.city?.value || ""}
            error={profile?.city?.error}
          />
          <YhInput
            label="Address 1"
            name="address1"
            placeholder="Address 1"
            onChange={handleOnChangeInputField}
            value={profile?.address1?.value || ""}
            error={profile?.address1?.error}
          />
          <YhInput
            label="Address 2"
            name="address2"
            placeholder="Address 2"
            onChange={handleOnChangeInputField}
            value={profile?.address2?.value || ""}
            error={profile?.address2?.error}
          />
          <YhInput
            type="text"
            label="Zip code"
            name="zipcode"
            placeholder="111111"
            onChange={handleOnChangeInputField}
            value={profile?.zipcode?.value || ""}
            error={profile?.zipcode?.error}
          />
          <YhInput
            label="Company Website"
            name="website"
            placeholder="www.yhataw.com"
            onChange={handleOnChangeInputField}
            value={profile?.website?.value || ""}
            error={profile?.website?.error}
          />
        </div>

        <Box sx={{ mt: 3, display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <YhButton
            variant="secondary"
            size="sm"
            onClick={() => history.push(urls.accountSettingsViewPth)}
          >
            Back
          </YhButton>
          <YhButton
            variant="primary"
            size="sm"
            loading={savingProfile}
            onClick={saveCompanyDetails}
          >
            Save Company Details
          </YhButton>
          {selectedOrgId && (
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                history.push(
                  `${urls.accountIntegrationsPath}?org=${selectedOrgId}`
                )
              }
            >
              Integrations
            </Button>
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <div className="mb-3">
          <h2 className="text-base font-semibold text-[#111827]">
            Lead phone masking
          </h2>
          <p className="mt-1 text-sm text-[#6B7280]">
            Settings apply only to the organization selected above
            {selectedOrg
              ? ` (${selectedOrg.name || selectedOrg.brandName})`
              : ""}
            . Full numbers always stay stored in the database. When masking is
            enabled, users see masked digits (e.g. ******3210) unless their
            role, team, or account is allowed below. When masking is disabled,
            everyone in this organization sees full numbers.
          </p>
          {!selectedOrgId && (
            <p className="mt-2 text-sm font-medium text-[#B45309]">
              Select an organization first to configure masking.
            </p>
          )}
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-4">
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Masking</InputLabel>
            <Select
              label="Masking"
              value={maskEnabled ? "on" : "off"}
              onChange={(e) => setMaskEnabled(e.target.value === "on")}
              disabled={!selectedOrgId}
            >
              <MenuItem value="on">Enabled (default)</MenuItem>
              <MenuItem value="off">Disabled — show full numbers</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="number"
            label="Visible last digits"
            value={visibleDigits}
            onChange={(e) =>
              setVisibleDigits(Math.max(1, Math.min(8, Number(e.target.value) || 4)))
            }
            inputProps={{ min: 1, max: 8 }}
            sx={{ width: 160 }}
            disabled={!selectedOrgId || !maskEnabled}
          />
          <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
            Preview:{" "}
            {maskEnabled
              ? `${"*".repeat(Math.max(0, 10 - visibleDigits))}${"9876543210".slice(-visibleDigits)}`
              : "9876543210"}
          </Typography>
        </div>

        <Typography sx={{ mb: 1, fontSize: 13, fontWeight: 600 }}>
          Who can see full numbers
          {selectedOrg
            ? ` in ${selectedOrg.name || selectedOrg.brandName}`
            : ""}
        </Typography>
        <Autocomplete
          multiple
          disabled={!selectedOrgId || !maskEnabled}
          options={["super_admin", "org_admin", "sub_admin", "manager", "agent"]}
          value={allowRoles}
          onChange={(_e, v) => setAllowRoles(v)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option}
                size="small"
                label={option}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Roles"
              size="small"
              sx={{ mb: 2 }}
              helperText="These roles see full numbers for this organization only"
            />
          )}
        />
        <Autocomplete
          multiple
          disabled={!selectedOrgId || !maskEnabled}
          options={routingTeams}
          getOptionLabel={(o) => o.teamName || o.teamId}
          isOptionEqualToValue={(a, b) => a.teamId === b.teamId}
          value={routingTeams.filter((t) => allowTeamIds.includes(t.teamId))}
          onChange={(_e, v) => setAllowTeamIds(v.map((t) => t.teamId))}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Teams allowed to see full number"
              size="small"
              sx={{ mb: 2 }}
              placeholder="Optional — teams in this organization"
            />
          )}
        />
        <Autocomplete
          multiple
          disabled={!selectedOrgId || !maskEnabled}
          options={routingMembers}
          getOptionLabel={(o) =>
            o.email ? `${o.name} (${o.email})` : o.name
          }
          isOptionEqualToValue={(a, b) => a.userId === b.userId}
          value={routingMembers.filter((m) => allowUserIds.includes(m.userId))}
          onChange={(_e, v) => setAllowUserIds(v.map((m) => m.userId))}
          renderInput={(params) => (
            <TextField
              {...params}
              label="People allowed to see full number"
              size="small"
              sx={{ mb: 2 }}
              placeholder="Optional — members of this organization"
            />
          )}
        />

        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <YhButton
            variant="primary"
            size="sm"
            loading={savingMask}
            disabled={!selectedOrgId}
            onClick={savePhoneMasking}
          >
            Save phone masking
            {selectedOrg
              ? ` for ${selectedOrg.name || selectedOrg.brandName}`
              : ""}
          </YhButton>
        </Box>
      </Paper>

      {canManage && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Tenant administration
          </Typography>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography sx={{ mb: 1, fontWeight: 500 }}>
              Create organization (full company profile)
            </Typography>
            <Typography sx={{ mb: 1.5, fontSize: 12, color: "#6B7280" }}>
              Enter company details below — slug is generated from brand name.
              Organization admin is optional: create one now, pick an existing
              team member, or skip and assign later.
            </Typography>
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              }}
            >
              <TextField
                size="small"
                label="Company Legal Name *"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (!slug) {
                    setSlug(
                      e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")
                        .replace(/^-|-$/g, "")
                    );
                  }
                }}
              />
              <TextField
                size="small"
                label="Brand / slug"
                value={slug}
                onChange={(e) =>
                  setSlug(
                    e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, "-")
                      .replace(/^-|-$/g, "")
                  )
                }
                helperText="Used as slug (e.g. home-tatva)"
              />
            </Box>

            <Typography sx={{ mt: 2.5, mb: 1, fontWeight: 500, fontSize: 14 }}>
              Organization admin (optional)
            </Typography>
            <FormControl size="small" sx={{ mb: 1.5, minWidth: 320 }}>
              <InputLabel>Admin source</InputLabel>
              <Select
                label="Admin source"
                value={adminMode}
                onChange={(e) =>
                  setAdminMode(
                    e.target.value as "none" | "new" | "existing"
                  )
                }
              >
                <MenuItem value="none">
                  Do not create admin now (org only)
                </MenuItem>
                <MenuItem value="new">
                  Create new admin (email + password)
                </MenuItem>
                <MenuItem value="existing">
                  Use existing team / CRM user
                </MenuItem>
              </Select>
            </FormControl>

            {adminMode === "none" && (
              <Typography sx={{ mb: 1, fontSize: 12, color: "#6B7280" }}>
                Organization will be created without an admin. You can assign
                one later in &quot;Assign organization admin / member&quot;
                below.
              </Typography>
            )}

            {adminMode === "new" ? (
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                }}
              >
                <TextField
                  size="small"
                  label="Admin name"
                  value={adminName}
                  onChange={(e) => setAdminName(e.target.value)}
                />
                <TextField
                  size="small"
                  label="Admin phone"
                  value={adminPhone}
                  onChange={(e) => setAdminPhone(e.target.value)}
                />
                <TextField
                  size="small"
                  required
                  type="email"
                  label="Admin email *"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                />
                <TextField
                  size="small"
                  required
                  type="password"
                  label="Admin password *"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  helperText="Min 6 characters — used to log into this org"
                />
              </Box>
            ) : null}

            {adminMode === "existing" ? (
              <Autocomplete
                size="small"
                sx={{ maxWidth: 480 }}
                options={crmUsers}
                value={createAdminUser}
                getOptionLabel={(opt) => opt.label}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                onChange={(_, value) => setCreateAdminUser(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select team member as org admin *"
                    placeholder="Search name or email"
                  />
                )}
              />
            ) : null}

            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                disabled={loading}
                onClick={handleCreate}
                sx={{ background: "#A02448" }}
              >
                {adminMode === "none"
                  ? "Create organization"
                  : "Create organization with admin"}
              </Button>
            </Box>
          </Paper>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography sx={{ mb: 1, fontWeight: 500 }}>
              Assign organization admin / member
            </Typography>
            <Typography sx={{ mb: 1.5, fontSize: 12, color: "#6B7280" }}>
              Select an existing CRM user. They will only see this
              organization&apos;s data in the header and lists after they log in
              again. Use Remove below to unassign.
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Organization</InputLabel>
                <Select
                  label="Organization"
                  value={memberOrgId || ""}
                  onChange={(e) => setMemberOrgId(String(e.target.value))}
                >
                  {orgs.map((o) => (
                    <MenuItem key={String(o._id)} value={String(o._id)}>
                      {o.name || o.brandName || o.slug || "Organization"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Autocomplete
                size="small"
                sx={{ minWidth: 320, flex: 1, maxWidth: 420 }}
                options={crmUsers}
                value={selectedMember}
                getOptionLabel={(opt) => opt.label}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                onChange={(_, value) => {
                  setSelectedMember(value);
                  setMemberUserId(value?.id || "");
                  setMemberEmail(value?.email || "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select CRM user"
                    placeholder="Search name or email…"
                  />
                )}
                noOptionsText={
                  crmUsers.length === 0
                    ? "No users loaded"
                    : "No matching user"
                }
              />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  label="Role"
                  value={memberRole}
                  onChange={(e) => setMemberRole(String(e.target.value))}
                >
                  <MenuItem value="org_admin">Org Admin</MenuItem>
                  <MenuItem value="sub_admin">Sub Admin</MenuItem>
                  <MenuItem value="manager">Manager</MenuItem>
                  <MenuItem value="agent">Agent</MenuItem>
                </Select>
              </FormControl>
              <Button variant="outlined" onClick={assignMember}>
                Assign
              </Button>
            </Box>

            <Typography sx={{ mt: 3, mb: 1, fontWeight: 500 }}>
              Assigned people
              {memberOrgId
                ? ` · ${
                    orgs.find((o) => String(o._id) === String(memberOrgId))
                      ?.name ||
                    orgs.find((o) => String(o._id) === String(memberOrgId))
                      ?.brandName ||
                    ""
                  }`
                : ""}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orgMembers.map((m) => (
                  <TableRow key={String(m._id)}>
                    <TableCell>
                      {m.user?.name || String(m.userId).slice(0, 8)}
                    </TableCell>
                    <TableCell>{m.user?.email || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={String(m.roleSlug || "agent").replace(/_/g, " ")}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        color="error"
                        disabled={removingMemberId === String(m._id)}
                        onClick={() => setMemberToRemove(m)}
                      >
                        {removingMemberId === String(m._id)
                          ? "Removing…"
                          : "Remove"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!orgMembers.length && !loadingMembers && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      No one assigned to this organization yet.
                    </TableCell>
                  </TableRow>
                )}
                {loadingMembers && (
                  <TableRow>
                    <TableCell colSpan={4}>Loading assigned people…</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>

          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Slug</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orgs.map((org) => (
                  <TableRow key={String(org._id)}>
                    <TableCell>
                      {org.legalName || org.name || "—"}
                    </TableCell>
                    <TableCell>{org.slug || "—"}</TableCell>
                    <TableCell>{org.brandName || org.name || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={org.status}
                        color={
                          org.status === "active" ? "success" : "default"
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => setSelectedOrgId(String(org._id))}
                      >
                        Edit details
                      </Button>
                      <Button
                        size="small"
                        onClick={() =>
                          history.push(
                            `${urls.accountIntegrationsPath}?org=${org._id}`
                          )
                        }
                      >
                        Integrations
                      </Button>
                      <Button size="small" onClick={() => toggleStatus(org)}>
                        {org.status === "suspended" ? "Activate" : "Suspend"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!orgs.length && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      No organizations yet. Run migration:{" "}
                      <code>npm run migrate:org</code>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      <ConfirmationModel
        open={!!memberToRemove}
        title="Remove from organization"
        description={`Remove ${memberRemoveLabel(
          memberToRemove
        )} from this organization? They will lose access to its data.`}
        confirmButtonText={removingMemberId ? "Removing…" : "Remove"}
        cancelButtonText="Cancel"
        onConfirm={confirmUnassignMember}
        onCancel={() => {
          if (removingMemberId) return;
          setMemberToRemove(null);
        }}
      />
    </Box>
  );
};

export default OrganizationsAdmin;
